<template>
  <div v-if="items" class="fin-financial-calendar mt-12 mb-12">
    <v-row>
      <v-col>
        <v-timeline align-top
                    dense>
          <v-slide-x-reverse-transition
              group
              hide-on-leave
          >
            <v-timeline-item
                v-for="day in itemsPerDate"
                :key="day.date.toDateString()"
                :hide-dot="true"
                right>
              <v-card class="date-card"
                      height="80px"
                      ref="dateCard"
                      slot="opposite"
                      width="90px">
                <v-card-text>
                  <div class="date-card__day">
                    {{ formatDate(day.date).day }}
                  </div>
                  <div class="date-card__month_year">
                    {{ formatDate(day.date).month }}
                    {{ formatDate(day.date).year }}
                  </div>
                </v-card-text>
              </v-card>

              <v-card :elevation="0"
                      :key="item.id"
                      class="event-card"
                      v-for="item in day.items">
                <v-card-title class="headline">{{ item.title }}</v-card-title>
                <v-card-text>
                  <span class="footnote" v-if="item.exchange.name">
                  Börse: {{ item.exchange.name }}
                  </span>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-slide-x-reverse-transition>
        </v-timeline>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'fin-financial-calendar',
  props: {
    items: {
      type: Array
    }
  },
  methods: {
    /**
     * @param unixTimestamp A unix timestamp as Number
     * @returns {Date} A Date-Object
     */
    convertTimestamp(unixTimestamp) {
      let result
      try {
        result = new Date(unixTimestamp * 1000)
      } catch (e) {
        result = unixTimestamp
      }
      return result
    },
    /**
     * Compares 2 dates with each other (Year/Month/Day). Ignores time
     *
     * @param date1 Compare date 1
     * @param date2 Compare date 2
     * @returns {boolean} True when they are the same, otherwise false
     */
    compareDate(date1, date2) {
      return (
          date1.getDate() === date2.getDate() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getFullYear() === date2.getFullYear()
      )
    },
    /**
     * Formats a date
     *
     * @param date Date to format
     * @returns {{month: string, year: string, day: string}} Formatted year/month/day in object
     */
    formatDate(date) {
      const ye = new Intl.DateTimeFormat('de', {year: 'numeric'}).format(date)
      const mo = new Intl.DateTimeFormat('de', {month: 'short'}).format(date)
      const da = new Intl.DateTimeFormat('de', {day: '2-digit'}).format(date)
      return {
        year: ye,
        month: mo,
        day: da
      }
    },
    /**
     * Make date-cards sticky
     */
    stickyCards() {
      // All cards
      const cards = this.$refs.dateCard
      cards.forEach(card => {
        // Height of timeline-item
        const duration = card.$el.parentElement.parentElement.clientHeight
        // Sticky beginPos and endPos for each card
        const beginPos = this.offsetTop(card.$el)
        const endPos = beginPos + duration - card.$el.clientHeight

        window.addEventListener('scroll', () => {
          const scrollPos = window.scrollY
          // If scroll is in between beginPos and endPos
          if (scrollPos > beginPos && scrollPos < endPos) {
            card.$el.style.top = `${scrollPos - beginPos}px`
          }
        })
      })
    },
    /**
     *
     * @param el HTML-Element
     * @return Number Returns the offset to the top of the page
     */
    offsetTop(el) {
      const rect = el.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      return rect.top + scrollTop
    }
  },
  computed: {
    /**
     * Groups this.items per date
     */
    itemsPerDate() {
      const result = []
      this.items.forEach(item => {
        const currDate = this.convertTimestamp(item.date)
        // Find if date already exists
        const currObj = result.find(newItem => {
          return this.compareDate(newItem.date, currDate)
        })

        try {
          // Try to push item to currObj
          currObj.items.push(item)
        } catch (e) {
          // If currObj is undefined then create a new object
          result.push({
            date: currDate,
            items: [item]
          })
        }
      })
      return result
    }
  },
  mounted() {
    if (this.items) {
      this.stickyCards()
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-card {
    &::before,
    &::after {
      display: none !important;
      background-color: transparent;
    }

    &.event-card {
      background-color: transparent !important;
    }

    .headline {
      font-family: $body-font-family !important;
      font-size: 1.2em;
      font-weight: bold;
      line-height: 1.25;
      padding-bottom: 0;
    }
  }

  .v-timeline-item {
    position: relative;
  }

  .v-timeline-item__opposite {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    text-align: center !important;
  }

}

.date-card {
  box-shadow: $fin-color-card-shadow !important;
  display: flex;
  justify-content: center;
  align-content: center;

  .v-card__text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
  }

  .v-card__text,
  .date-card__day,
  .date-card__month_year {
    color: $fin-color-primary-dark;
    padding: 0 !important;
  }

  .date-card__day {
    font-family: $body-font-family;
    font-size: 2.2em;
    font-weight: bold;
    line-height: 1.19;
  }

  .date-card__month_year {
    text-transform: uppercase;
    font-family: $body-font-family;
    font-size: 0.8em;
    font-weight: 600;
    line-height: 1.17;
    letter-spacing: 1px;
  }
}

.footnote {
  font-size: 0.8em;
  color: $fin-color-primary-dark !important;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  font-family: $body-font-family;
}
</style>
